import { useMemo } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { PRODUCTS, setActiveProduct } from '../../utils/urlConfig';
import VerificationScreen from '../onboarding/verificationScreen';

const ProductResolver = () => {
  const { productId, sno, lot } = useParams();
  const location = useLocation();

  // Resolve product key AND sync localStorage in the same pass.
  // useMemo runs synchronously before the render completes, so
  // VerificationScreen receives a fresh productKey on its very first render
  // — no useEffect delay, no stale-localStorage problem.
  const { resolvedProduct, resolvedHash } = useMemo(() => {
    for (const [key, config] of Object.entries(PRODUCTS)) {
      const gtinMatch = productId && (config.gtin === productId || (config.gtins && config.gtins.includes(productId)));
      if (!gtinMatch) continue;

      // Check default serial number first
      if (sno && config.serialNumber === sno) {
        setActiveProduct(key);
        return { resolvedProduct: key, resolvedHash: location.hash };
      }

      // Search all qrCodes entries for the serial number
      if (sno && config.qrCodes) {
        for (const qrEntry of Object.values(config.qrCodes)) {
          if (qrEntry.serialNumber === sno) {
            setActiveProduct(key);
            // Derive hash from qrEntry status or hash field
            const entryHash = qrEntry.hash
              ? (qrEntry.hash.startsWith('#') ? qrEntry.hash : `#${qrEntry.hash}`)
              : `#${qrEntry.status}`;
            return { resolvedProduct: key, resolvedHash: entryHash };
          }
        }
      }

      // GTIN matched but no serial match — still resolve the product
      if (!sno) {
        setActiveProduct(key);
        return { resolvedProduct: key, resolvedHash: location.hash };
      }
    }
    return { resolvedProduct: null, resolvedHash: '' };
  }, [productId, sno, location.hash]);

  if (!resolvedProduct) {
    return (
      <div className="flex items-center justify-center h-screen bg-gray-100">
        <div className="text-center p-6 bg-white rounded-lg shadow-md max-w-md mx-auto">
          <h2 className="text-xl font-bold text-red-600 mb-2">Product Not Found</h2>
          <p className="text-gray-600 mb-4">The requested product identifier matches no records in our system.</p>

          <div className="bg-gray-50 p-3 rounded text-xs text-gray-500 text-left border border-gray-200">
            <p className="font-semibold mb-1">Debug Information:</p>
            <p>GTIN (01): {productId || 'N/A'}</p>
            <p>Serial (21): {sno || 'N/A'}</p>
            <p>Lot (10): {lot || 'N/A'}</p>
            <p>Available Products: {Object.keys(PRODUCTS).join(', ')}</p>
          </div>
        </div>
      </div>
    );
  }

  // Pass both the resolved key and the hash fragment as explicit props so
  // VerificationScreen does not need to re-derive either from localStorage
  // or from window.location (both of which can be stale at mount time).
  return (
    <VerificationScreen
      productKey={resolvedProduct}
      hashFragment={resolvedHash}
      urlSerialNumber={sno}
    />
  );
};

export default ProductResolver;
